import {
  Dialog,
  DialogProps as MuiDialogProps,
  Drawer as MuiDrawer,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  DrawerProps
} from '@mui/material';
import { IDialogProps } from '../types';
import { CSSObject, styled } from '@mui/material/styles';

const panelMixin = ({ theme, panelOpen, panelExpand }: any): CSSObject => ({
  transition: `padding 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms`,
  paddingRight: `48px`,
  ...(panelOpen && {
    transition: `padding 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms`,
    paddingRight: `336px`
  }),
  [theme.breakpoints.down('lg')]: {
    paddingRight: `48px`,
    ...(panelOpen && {
      paddingRight: `48px`
    })
  }
});

const typeMixin = ({ theme, type, panelOpen, width }: any): CSSObject => ({
  ...(type === 'confirm' && {
    '.MuiDialog-container': {
      '> .MuiPaper-root': {
        minWidth: `480px`,
        maxWidth: `1062px`,
        width: `${width}px`,
        padding: `24px`,
        boxShadow: `0 3px 6px 0 rgba(0, 0, 0, 0.24)`,
        borderRadius: `2px`,
        boxSizing: `border-box`,
        ...(panelOpen && panelMixin({ theme, panelOpen })),
        '.MuiDialogTitle-root': {
          fontSize: `18px`,
          fontWeight: `bold`
        },
        '.MuiDialogContent-root': {
          paddingTop: `32px`,
          paddingBottom: `32px`
        },
        '.MuiDialogActions-root': {
          button: {
            '+ button': {
              marginLeft: `6px`
            }
          }
        }
      }
    }
  }),
  ...(type === 'alert' && {
    '.MuiDialog-container': {
      '> .MuiPaper-root': {
        minWidth: `340px`,
        maxWidth: `1062px`,
        width: `${width}px`,
        padding: `24px`,
        boxShadow: `0 3px 6px 0 rgba(0, 0, 0, 0.24)`,
        borderRadius: `2px`,
        boxSizing: `border-box`,
        ...(panelOpen && panelMixin({ theme, panelOpen })),
        '.MuiDialogTitle-root': {},
        '.MuiDialogContent-root': {
          paddingBottom: `32px`,
          fontSize: `16px`,
          fontWeight: `500`
        },
        '.MuiDialogActions-root': {
          button: {
            '+ button': {
              marginLeft: `4px`
            }
          }
        }
      }
    }
  }),
  ...(type === 'simple' && {
    '.MuiDialog-container': {
      '> .MuiPaper-root': {
        minWidth: `220px`,
        maxWidth: `540px`,
        width: `${width}px`,
        padding: `20px`,
        border: `1px solid ${theme.palette.secondary[600]}`,
        boxShadow: `0 2px 4px 0 rgba(0, 0, 0, 0.24)`,
        borderRadius: `2px`,
        boxSizing: `border-box`,
        ...(panelOpen && panelMixin({ theme, panelOpen })),
        '.MuiDialogTitle-root': {
          fontSize: `15px`,
          fontWeight: `bold`
        },
        '.MuiDialogContent-root': {
          paddingTop: `24px`,
          paddingBottom: `24px`
        },
        '.MuiDialogActions-root': {}
      }
    }
  }),
  ...(type === 'panel' && {
    '.MuiDialog-container': {
      '> .MuiPaper-root': {
        border: `1px solid ${theme.palette.secondary[600]}`,
        boxShadow: `0 2px 4px 0 rgba(0, 0, 0, 0.24)`,
        borderRadius: `2px`,
        margin: `60px`,
        maxHeight: `calc(100% - 120px)`,
        maxWidth: `1280px`,
        minWidth: `648px`,
        width: `${width}px`,
        boxSizing: `border-box`,
        ...(panelOpen != null && panelMixin({ theme, panelOpen })),
        '.dialog-header': {
          display: `flex`,
          alignItems: ` center`,
          justifyContent: `space-between`,
          height: `72px`,
          padding: `24px`,
          boxSizing: `border-box`
        },
        '.MuiDialogContent-root': {
          padding: `8px 24px 24px`
        },
        '.MuiDialogActions-root': {
          padding: `24px`,
          borderTop: `1px solid ${theme.palette.secondary[100]}`
        }
      }
    }
  })
});

export const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) =>
    !['panelOpen', 'panelExpand', 'type', 'width', 'headerClose'].includes(String(prop))
})<IDialogProps & MuiDialogProps>(
  ({ theme, panelOpen, panelExpand, type, width, headerClose }) => ({
    '.MuiDialog-container': {
      '> .MuiPaper-root': {
        minWidth: `600px`,
        padding: `24px`,
        boxShadow: `0 3px 6px 0 rgba(0, 0, 0, 0.24)`,
        borderRadius: `2px`,
        boxSizing: `border-box`,
        '.MuiDialogTitle-root': {
          fontSize: `18px`,
          fontWeight: `bold`
        },
        '.MuiDialogContent-root': {
          paddingTop: `32px`,
          paddingBottom: `32px`
        },
        '.MuiDialogActions-root': {
          button: {
            '+ button': {
              marginLeft: `6px`
            }
          }
        }
      }
    },

    '.dialog-header': {
      display: `flex`,
      alignItems: `center`,
      justifyContent: `space-between`,
      '.MuiTypography-root ': {
        '&.MuiDialogTitle-root': {
          fontSize: `18px`,
          fontWeight: `bold`
        }
      }
    },
    // ...(panel != null && panelMixin({ theme, panel, drawer })),
    ...(type != null && typeMixin({ theme, type, panelOpen, panelExpand, width }))
  })
);

export const DialogPanel = styled(MuiDrawer)<DrawerProps>(({ theme, variant, open }) => ({
  '.MuiPaper-root': {
    width: `48px`,
    padding: `8px`,
    boxSizing: `border-box`,
    overflowX: `hidden`,
    transition: `width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms`
  },
  ...(open && {
    width: `336px`,
    padding: `0`,
    '.MuiPaper-root': {
      width: `336px`,
      overflow: `overlay`,
      transition: `width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms`
    }
  })
}));

export const DialogHeader = styled('div')(({ theme }) => ({
  '&.sticky': {
    boxShadow: `0 2px 2px 0 rgba(0, 0, 0, 0.12)`
  }
}));

export const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  padding: `0`,
  flex: 1
}));

export const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: `0`
}));

export const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
  padding: `0`
}));

//
// const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
//   open?: boolean;
// }>(({ theme, drawer, fullWidth }) => ({
//   padding: theme.spacing(3),
//   marginRight: `-240px`,
//   ...(drawer && {
//     marginRight: 0,
//   }),
// }));
//
// export const StyledDialog = styled(Dialog)<DialogProps & MuiDialogProps>(({ theme, fullWidth, drawer }) => ({
//   '& .MuiPaper-root' :{
//     minWidth: `340px`
//   },
//   ...(fullWidth && {
//     '& .MuiPaper-root' :{
//       maxWidth: `none`
//     },
//   }),
//   ...(drawer && {
//     '& .MuiPaper-root' :{
//       backgroundColor: `red`
//     },
//   })
// // }));
//
// export const DialogWrap = styled('div')(({ theme, color }) => ({
// display: `flex`,
//   flexDirection: `column`
//
// }));

//
// import {styled, CSSObject, alpha} from '@mui/system';
// import {
//   Dialog as MuiDialog,
//   DialogTitle as MuiDialogTitle,
//   DialogContent as MuiDialogContent,
//   DialogActions as MuiDialogActions,
//   Drawer as MuiDrawer,
// } from "@mui/material";
//
// export const DialogTitle = styled(MuiDialogTitle)(
//   ({ theme }:any):CSSObject => ({
//
//   })
// );
//
// export const DialogContent = styled(MuiDialogContent)(
//   ({ theme }:any):CSSObject => ({
//
//   })
// );
//
// export const DialogActions = styled(MuiDialogActions)(
//   ({ theme }:any):CSSObject => ({
//
//   })
// );
//
// export const Dialogpanel = styled(MuiDrawer)(
//   ({ theme }:any):CSSObject => ({
//
//   })
// );
//
// export const DialogWrap = styled(MuiDialog)(
//   ({ theme }:any):CSSObject => ({
//
//   })
// );

// export const DialogTitle = styled(MuiDialogTitle)``;

// export const DialogContent = styled(MuiDialogContent)``;

// export const DialogActions = styled(MuiDialogActions)``;

// export const Dialogpanel = styled(MuiDrawer)`

//   // animation-name: show;
//   // animation-duration: 0.3s;
//
//   // ${DialogTitle} {
//   //   font-weight: bold;
//   // }
//   //
//   // ${DialogContent} {
//   //
//   //   &.MuiDialogContent-root {
//   //     padding: 0 24px;
//   //   }
//   // }
//   //
//   // ${DialogActions} {
//   //   padding: 16px 24px;
//   //
//   //   .MuiButton-root {
//   //     padding: 8px 15px;
//   //     font-weight: bold;
//   //   }
//   // }
// `;
